"use client";
import React, { useState, useRef } from "react";
import InformationButton from "@/components/ui/InformationButton";

type UnitPosition = "left" | "right";

interface CalcInputValuesProps {
  label: string;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  modeOptions?: string[];
  defaultMode?: string;
  unitPosition?: UnitPosition;
  amountValue?: number;
  showFloatingValue?: boolean;
  keyWord?: string | null;
  onChange?: (value: number) => void; // send numeric value up
  onModeChange?: (mode: string) => void;

  amountMode?: string;
  showAmountModeToggle?: boolean; // whether to show % / ₹ toggle
  onAmountModeChange?: (mode: "₹" | "%") => void; // notify parent

  useRangeSlider?: boolean;
  useInputField?: boolean;
  allowDecimal?: boolean;
}

function formatCompactNumber(num: number): string {
  const units = [
    { value: 1_00_00_00_000, suffix: "B" },
    { value: 1_00_00_000, suffix: "Cr" },
    { value: 1_00_000, suffix: "L" },
    { value: 1_000, suffix: "k" },
  ];

  for (const { value, suffix } of units) {
    if (num >= value) {
      const formatted = num / value;
      return formatted.toFixed(2).replace(/\.?0+$/, "") + suffix;
    }
  }

  return num % 1 === 0 ? num.toString() : num.toFixed(2).replace(/\.?0+$/, "");
}

function formatIndianNumber(num: number): string {
  const [integerPart, decimalPart] = num.toString().split(".");

  const lastThree = integerPart.slice(-3);
  const otherNumbers = integerPart.slice(0, -3);

  let formattedInteger = integerPart;
  if (otherNumbers !== "") {
    formattedInteger =
      otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + lastThree;
  }

  return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
}

export default function CalcInputValues({
  label,
  min = 0,
  max = 0,
  step = 1,
  unit = "",
  modeOptions = [],
  defaultMode,
  amountValue,
  showFloatingValue = false,
  unitPosition = "right",
  keyWord,
  onChange,
  onModeChange,
  showAmountModeToggle = false,
  amountMode,
  onAmountModeChange,
  useRangeSlider = true,
  useInputField = true,
  allowDecimal = false,
}: CalcInputValuesProps) {
  const [value, setValue] = useState(amountValue ?? min);
  const [mode, setMode] = useState(defaultMode ?? modeOptions[0] ?? "");
  const initialNum = amountValue ?? min;
  const [inputValue, setInputValue] = useState(formatIndianNumber(initialNum));
  const [error, setError] = useState(false);

  const rangeRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [hasAdjusted, setHasAdjusted] = useState(false);

  const handleLabelClick = () => {
    if (inputRef.current && !hasAdjusted) {
      const input = inputRef.current;
      input.focus();
      const len = input.value.length;
      input.setSelectionRange(len, len);
      setHasAdjusted(true);
    }
  };

  const handleBlur = () => {
    setHasAdjusted(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let raw = e.target.value.replace(/,/g, "");

    const regex = allowDecimal ? /^\d*\.?\d*$/ : /^\d*$/;
    if (!regex.test(raw)) return;

    if (raw === "") {
      raw = "0";
    }

    let num = allowDecimal ? parseFloat(raw) : Number(raw);

    const isPartialDecimal = allowDecimal && raw.endsWith(".");
    const isValidNumber = !isNaN(num);

    if (isValidNumber) {
      if (num > max) {
        num = max;
        raw = max.toString();
      }

      if (allowDecimal) num = Math.round(num * 100) / 100;

      setValue(num);
      setError(num === 0);

      const formatted = isPartialDecimal
        ? formatIndianNumber(num) + "."
        : formatIndianNumber(num);

      setInputValue(formatted);
      onChange?.(num);
    } else {
      setInputValue(raw);
    }
  };

  const handleRangeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const num = Number(e.target.value);
    setValue(num);
    setInputValue(formatIndianNumber(num));
    setError(num === 0 ? true : false);
    onChange?.(num);
  };

  const handleModeChange = (opt: string) => {
    setMode(opt);
    onModeChange?.(opt);
  };

  const handleAmountModeChange = (mode: "₹" | "%") => {
    onAmountModeChange?.(mode);
  };

  const percentage = ((value - min) / (max - min)) * 100;

  const labelPositionClass =
    percentage < 25
      ? "translate-x-0"
      : percentage > 75
        ? "-translate-x-full"
        : "-translate-x-1/2";

  return (
    <div className="w-full">
      <div className="flex justify-between items-center gap-0 xxxs:gap-2 mb-2">
        <div className="flex items-center gap-1">
          <span className="font-semibold text-left text-2xs xxxs:text-sm sm:text-base lg:text-lg text-[#222222]">
            {label}
          </span>
          {keyWord && (
            <InformationButton
              keywords={keyWord}
              className="w-4! h-4! sm:w-5! sm:h-5!"
            />
          )}
        </div>

        {showAmountModeToggle && (
          <div className="flex items-center justify-between p-px sm:p-0.5 rounded-full border border-primaryGreen overflow-hidden w-half-px h-6 sm:w-15 sm:h-7.5 ml-auto mr-1 xxxs:mr-0">
            <button
              onClick={() => handleAmountModeChange("%")}
              className={`w-5 h-5 sm:w-6.25 sm:h-6.25 transition duration-350 rounded-full flex items-center justify-center text-sm ${amountMode === "%" ? "bg-primaryGreen text-white" : "text-primaryGreen bg-white"}`}
            >
              %
            </button>
            <button
              onClick={() => handleAmountModeChange("₹")}
              className={`w-5 h-5 sm:w-6.25 sm:h-6.25 transition duration-350 rounded-full flex items-center justify-center text-sm ${amountMode === "₹" ? "bg-primaryGreen text-white" : "text-primaryGreen bg-white"}`}
            >
              ₹
            </button>
          </div>
        )}

        {useInputField && (
          <label
            onClick={handleLabelClick}
            className={`max-w-24 xxxs:max-w-26.5 xs:max-w-40 relative flex justify-start rounded-lg overflow-hidden p-1.5 xs:py-2 xs:px-3.5 font-medium text-xs sm:text-sm lg:text-base transition border ${error ? "text-[#f90d0d] border-[#f90d0d] bg-[#fff3f3]" : "text-primaryGreen border-[#cad3e2] bg-white focus-within:border-[#a0a4ac] hover:border-[#a0a4ac]"}`}
          >
            {unitPosition == "left" && (
              <span
                className={`${error ? "text-[#f90d0da4]" : "text-[#006b66a4]"} inline-block absolute top-1/2 -translate-y-1/2 my-auto pointer-events-none`}
              >
                {unit}
              </span>
            )}
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onBlur={handleBlur}
              onChange={handleInputChange}
              className={`${unitPosition === "right" ? "" : "pl-2.5 xs:pl-4"} w-full bg-inherit`}
            />
            {unitPosition == "right" && (
              <span
                className={`${error ? "text-[#f90d0da4]" : "text-[#006b66a4]"} inline-block absolute right-4 top-1/2 -translate-y-1/2 my-auto transition-[left] duration-350 pointer-events-none`}
              >
                {unit}
              </span>
            )}
          </label>
        )}
      </div>

      {modeOptions.length > 0 && (
        <div className="flex items-center gap-2 mb-0.5 xs:mb-2">
          {modeOptions.map((opt) => (
            <button
              key={opt}
              onClick={() => handleModeChange(opt)}
              className={`px-3 xs:px-4 py-1 xs:py-1.5 text-xs xs:text-sm xs:leading-5 md:text-base md:leading-6 leading-4 w-full max-w-20 xs:max-w-24 md:max-w-32 text-center rounded-full border font-medium transition duration-350 ${mode === opt ? "bg-primaryGreen text-white border-primaryGreen" : "border-gray-400 text-gray-700 hover:bg-[#f4fffe] hover:border-primaryGreen"}`}
            >
              {opt}
            </button>
          ))}
        </div>
      )}

      {/* Range slider */}
      {useRangeSlider && (
        <div className="relative">
          <input
            ref={rangeRef}
            type="range"
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={(e) => handleRangeChange(e)}
            style={{
              background: `linear-gradient(to right, #006b66 0%, #006b66 ${percentage}%, #d4d4d4 ${percentage}%, #d4d4d4 100%)`,
            }}
            aria-label={label}
            className={`w-full h-1.5 rounded-lg appearance-none focus:outline-hidden
            [&::-webkit-slider-thumb]:appearance-none 
            [&::-webkit-slider-thumb]:w-4.5 
            [&::-webkit-slider-thumb]:h-4.5 
            xs:[&::-webkit-slider-thumb]:w-5.5 
            xs:[&::-webkit-slider-thumb]:h-5.5 
            [&::-webkit-slider-thumb]:bg-white 
            [&::-webkit-slider-thumb]:border-[3px] 
            xs:[&::-webkit-slider-thumb]:border-4 
            [&::-webkit-slider-thumb]:border-solid 
            [&::-webkit-slider-thumb]:border-primaryGreen 
            [&::-webkit-slider-thumb]:rounded-full 
            [&::-webkit-slider-thumb]:cursor-pointer
            [&::-moz-range-thumb]:appearance-none
            [&::-moz-range-thumb]:w-4.5 
            [&::-moz-range-thumb]:h-4.5 
            xs:[&::-moz-range-thumb]:w-5.5 
            xs:[&::-moz-range-thumb]:h-5.5 
            [&::-moz-range-thumb]:bg-white 
            [&::-moz-range-thumb]:border 
            [&::-moz-range-thumb]:border-primaryGreen 
            [&::-moz-range-thumb]:rounded-full 
            [&::-moz-range-thumb]:cursor-pointer
            [&::-ms-thumb]:appearance-none`}
          />
          {showFloatingValue && (
            <div
              className={`absolute -bottom-4.5 xs:-bottom-6 transition duration-300 ${labelPositionClass} text-teal-700 font-semibold text-xs sm:text-sm whitespace-nowrap`}
              style={{ left: `calc(${percentage}%)` }}
            >
              {unit}
              {formatCompactNumber(value)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
